import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import useActivityLog from '../hooks/useActivityLog';
import './ActivityHistory.css';

/**
 * Vista completa del historial de actividad
 * Página dedicada con filtros y estadísticas
 */
const ActivityHistory = () => {
  const [filter, setFilter] = useState('all'); // 'all', 'checklist', 'status', 'observation'
  const [dateRange, setDateRange] = useState('all'); // 'today', 'week', 'month', 'all'
  const [searchQuery, setSearchQuery] = useState('');

  const { 
    getActivityByDate, 
    getActivityGroupedByDay,
    getStats,
    clearOldActivity,
    clearAll
  } = useActivityLog();

  const stats = getStats();
  const groupedActivities = getActivityGroupedByDay();

  // Filtrar actividades
  const filteredGroups = useMemo(() => {
    let activities = dateRange === 'all' 
      ? groupedActivities 
      : groupedActivities.filter(group => {
          const groupDate = new Date(group.date);
          const now = new Date();
          const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          
          switch (dateRange) {
            case 'today':
              return groupDate >= startOfDay;
            case 'week':
              return groupDate >= new Date(startOfDay.getTime() - 7 * 24 * 60 * 60 * 1000);
            case 'month':
              return groupDate >= new Date(startOfDay.getTime() - 30 * 24 * 60 * 60 * 1000);
            default:
              return true;
          }
        });

    // Filtrar por tipo
    if (filter !== 'all') {
      activities = activities.map(group => ({
        ...group,
        items: group.items.filter(item => {
          switch (filter) {
            case 'checklist':
              return item.type === 'checklist_item';
            case 'status':
              return item.type === 'status_change';
            case 'observation':
              return item.type === 'observation';
            default:
              return true;
          }
        })
      })).filter(group => group.items.length > 0);
    }

    // Filtrar por búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      activities = activities.map(group => ({
        ...group,
        items: group.items.filter(item => 
          item.taskName?.toLowerCase().includes(query) ||
          item.details?.itemText?.toLowerCase().includes(query) ||
          item.details?.text?.toLowerCase().includes(query)
        )
      })).filter(group => group.items.length > 0);
    }

    return activities;
  }, [groupedActivities, dateRange, filter, searchQuery]);

  // Formatear fecha del grupo
  const formatGroupDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Hoy';
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Ayer';
    }
    return date.toLocaleDateString('es-ES', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  // Formatear hora
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtener icono según tipo
  const getActivityIcon = (activity) => {
    switch (activity.type) {
      case 'checklist_item':
        return activity.action === 'completed' ? '✅' : '⬜';
      case 'status_change':
        const statusIcons = {
          pending: '📦',
          progress: '🔄',
          testing: '🧪',
          completed: '✅'
        };
        return statusIcons[activity.details?.to] || '🔄';
      case 'observation':
        return '💬';
      case 'timer':
        return '⏱️';
      default:
        return '📋';
    }
  };

  // Obtener descripción de actividad
  const getActivityDescription = (activity) => {
    switch (activity.type) {
      case 'checklist_item':
        const action = activity.action === 'completed' ? 'Verificado' : 'Desmarcado';
        return (
          <>
            <span className="history-action">{action}:</span>
            <span className="history-detail">"{activity.details?.itemText}"</span>
          </>
        );
      case 'status_change':
        const statusNames = {
          pending: 'Pendiente',
          progress: 'En Desarrollo',
          testing: 'Testing',
          completed: 'Completado'
        };
        return (
          <>
            <span className="history-action">Estado cambiado:</span>
            <span className="history-detail">
              {statusNames[activity.details?.from]} → {statusNames[activity.details?.to]}
            </span>
          </>
        );
      case 'observation':
        return (
          <>
            <span className="history-action">
              Observación {activity.action === 'added' ? 'añadida' : 'editada'}:
            </span>
            {activity.details?.text && (
              <span className="history-detail">"{activity.details.text.substring(0, 100)}..."</span>
            )}
          </>
        );
      case 'timer':
        return (
          <>
            <span className="history-action">
              Timer {activity.action === 'started' ? 'iniciado' : activity.action === 'paused' ? 'pausado' : 'detenido'}
            </span>
          </>
        );
      default:
        return <span className="history-action">Actividad registrada</span>;
    }
  };

  // Color de la actividad
  const getActivityColor = (activity) => {
    if (activity.type === 'checklist_item' && activity.action === 'completed') return 'green';
    if (activity.type === 'status_change') {
      if (activity.details?.to === 'completed') return 'green';
      if (activity.details?.to === 'testing') return 'yellow';
      if (activity.details?.to === 'progress') return 'blue';
    }
    if (activity.type === 'observation') return 'purple';
    return 'gray';
  };

  // Contar total de actividades filtradas
  const totalFiltered = filteredGroups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <div className="activity-history-container">
      {/* Background */}
      <div className="history-bg">
        <div className="grid-overlay"></div>
        <div className="glow-orb orb-1"></div>
        <div className="glow-orb orb-2"></div>
      </div>

      {/* Header */}
      <header className="history-header">
        <div className="history-header-left">
          <Link to="/project/fixarr" className="history-back">
            ← Volver al Roadmap
          </Link>
          <h1>📜 Historial de Actividad</h1>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="history-stats">
        <div className="history-stat-card">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.today.total}</div>
            <div className="stat-label">Hoy</div>
          </div>
        </div>
        <div className="history-stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-value">{stats.week.total}</div>
            <div className="stat-label">Esta semana</div>
          </div>
        </div>
        <div className="history-stat-card accent">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.week.checklistCompleted}</div>
            <div className="stat-label">Verificaciones</div>
          </div>
        </div>
        <div className="history-stat-card">
          <div className="stat-icon">💬</div>
          <div className="stat-content">
            <div className="stat-value">{stats.week.observations}</div>
            <div className="stat-label">Observaciones</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="history-filters">
        <div className="filter-group">
          <label>Tipo:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">Todos</option>
            <option value="checklist">Checklist</option>
            <option value="status">Cambios de Estado</option>
            <option value="observation">Observaciones</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Período:</label>
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="today">Hoy</option>
            <option value="week">Esta semana</option>
            <option value="month">Este mes</option>
            <option value="all">Todo</option>
          </select>
        </div>
        <div className="filter-group search">
          <span className="search-icon">🔍</span>
          <input 
            type="text"
            placeholder="Buscar por tarea..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-results">
          {totalFiltered} {totalFiltered === 1 ? 'actividad' : 'actividades'}
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="history-timeline">
        {filteredGroups.length === 0 ? (
          <div className="history-empty">
            <span className="history-empty-icon">📭</span>
            <h3>No hay actividad</h3>
            <p>
              {searchQuery 
                ? 'No se encontraron resultados para tu búsqueda'
                : 'Marca items del checklist o cambia estados para ver actividad aquí'}
            </p>
          </div>
        ) : (
          filteredGroups.map(group => (
            <div key={group.date} className="history-day-group">
              <div className="history-day-header">
                <span className="history-day-date">{formatGroupDate(group.date)}</span>
                <span className="history-day-count">{group.items.length} actividades</span>
              </div>
              <div className="history-day-items">
                {group.items.map(activity => (
                  <div 
                    key={activity.id} 
                    className={`history-item color-${getActivityColor(activity)}`}
                  >
                    <div className="history-item-time">
                      {formatTime(activity.timestamp)}
                    </div>
                    <div className="history-item-line">
                      <div className="history-item-dot"></div>
                    </div>
                    <div className="history-item-content">
                      <div className="history-item-icon">
                        {getActivityIcon(activity)}
                      </div>
                      <div className="history-item-body">
                        <div className="history-item-task">
                          {activity.taskName}
                        </div>
                        <div className="history-item-description">
                          {getActivityDescription(activity)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Actions */}
      {filteredGroups.length > 0 && (
        <div className="history-actions">
          <button 
            className="history-action-btn secondary"
            onClick={() => {
              const removed = clearOldActivity();
              if (removed > 0) {
                alert(`Se eliminaron ${removed} actividades de hace más de 30 días`);
              } else {
                alert('No hay actividades antiguas que limpiar');
              }
            }}
          >
            🧹 Limpiar antiguas (+30 días)
          </button>
        </div>
      )}
    </div>
  );
};

export default ActivityHistory;
